import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  CloudArrowUpIcon, 
  FilmIcon,
  XMarkIcon 
} from '@heroicons/react/24/outline';

interface PreselectedUpload {
  fileId: string;
  name: string;
  size?: number;
}

interface VideoUploadZoneProps {
  onFileSelect: (file: File | null) => void;
  selectedFile: File | null;
  isUploading?: boolean;
  uploadProgress?: number;
  preselectedUpload?: PreselectedUpload | null;
  onClearPreselectedUpload?: () => void;
  /** When true, also accepts audio files (for Avatar-mode translations with no source video). */
  acceptAudio?: boolean;
}

const VideoUploadZone: React.FC<VideoUploadZoneProps> = ({
  onFileSelect,
  selectedFile,
  isUploading = false,
  uploadProgress,
  preselectedUpload,
  onClearPreselectedUpload,
  acceptAudio = false,
}) => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      onFileSelect(file);
    }
  }, [onFileSelect]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/*': ['.mp4', '.avi', '.mov', '.webm', '.wmv'],
      ...(acceptAudio ? { 'audio/*': ['.mp3', '.wav', '.m4a', '.aac', '.ogg', '.flac'] } : {}),
    },
    maxFiles: 1,
    maxSize: 500 * 1024 * 1024, // 500MB
    disabled: isUploading
  });

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const removeFile = () => {
    onFileSelect(null);
  };

  const displayFile = selectedFile || preselectedUpload;

  if (displayFile) {
    const isPreselected = !selectedFile && !!preselectedUpload;
    const displayName = selectedFile?.name || preselectedUpload?.name || '';
    const displaySize = selectedFile?.size || preselectedUpload?.size || 0;

    return (
      <div className="border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FilmIcon className="h-7 w-7 text-blue-500" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {displayName}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {isPreselected ? 'Ready to translate' : formatFileSize(displaySize)}
              </p>
            </div>
          </div>

          {!isUploading && (
            <button
              onClick={() => {
                if (isPreselected) {
                  onClearPreselectedUpload?.();
                } else {
                  removeFile();
                }
              }}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>

        {isUploading && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
              <span>
                {uploadProgress !== undefined && uploadProgress < 100
                  ? 'Uploading...'
                  : 'Starting translation...'}
              </span>
              {uploadProgress !== undefined && uploadProgress < 100 && (
                <span className="font-medium">{uploadProgress}%</span>
              )}
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-200"
                style={{
                  width: uploadProgress !== undefined && uploadProgress < 100
                    ? `${uploadProgress}%`
                    : '100%',
                  animation: uploadProgress === undefined || uploadProgress >= 100
                    ? 'pulse 1.5s ease-in-out infinite'
                    : 'none',
                }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      {...getRootProps()}
      className={`border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-colors ${
        isDragActive
          ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
      } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <input {...getInputProps()} />
      
      <CloudArrowUpIcon className="mx-auto h-9 w-9 text-gray-400" />
      
      <div className="mt-2">
        <p className="text-sm font-medium text-gray-900 dark:text-white">
          {isDragActive ? 'Drop file here' : acceptAudio ? 'Upload video or audio file' : 'Upload video file'}
        </p>
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
          {acceptAudio
            ? 'Drag and drop a video or audio file here, or click to browse · MP4, MOV, WebM, MP3, WAV, M4A (max 500MB)'
            : 'Drag and drop your video file here, or click to browse · MP4, AVI, MOV, WebM (max 500MB)'}
        </p>
      </div>
    </div>
  );
};

export default VideoUploadZone;